import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import {
    getGridMultiSelectOperators,
    getGridSingleSelectOperators,
    GridFilterInputValue,
    type GridColDef,
    type GridFilterOperator,
    type GridMultiSelectColDef,
    type GridValidRowModel,
    type ValueOptions
} from '@mui/x-data-grid-pro';

/** The filter panel's words for the operators below, in the UI's language. */
export interface ListFilterLabels {
    contains: string;
    equals: string;
    startsWith: string;
    endsWith: string;
    /** The built-in pick from the options: "has one of these". */
    containsAnyOf: string;
    doesNotContainAnyOf: string;
}

type TextMatch = (entry: string, typed: string) => boolean;

const collator = new Intl.Collator(undefined, {
    sensitivity: 'base',
    usage: 'search'
});

const lower = (text: string) => text.toLocaleLowerCase();

const TEXT_MATCHES: Record<
    'contains' | 'equals' | 'startsWith' | 'endsWith',
    TextMatch
> = {
    contains: (entry, typed) => lower(entry).includes(lower(typed)),
    equals: (entry, typed) => collator.compare(entry, typed) === 0,
    startsWith: (entry, typed) => lower(entry).startsWith(lower(typed)),
    endsWith: (entry, typed) => lower(entry).endsWith(lower(typed))
};

/**
 * What a list entry reads as in the cell: its option's label ("Bef. 7000,
 * Fl. 1, Pl. 1" for a Planum's id), or the entry itself.
 */
function entryLabeller(column: GridColDef): (entry: unknown) => string {
    const listColumn = column as unknown as GridMultiSelectColDef;
    const options = Array.isArray(listColumn.valueOptions)
        ? listColumn.valueOptions
        : [];
    const valueOf = (option: ValueOptions) =>
        listColumn.getOptionValue ? listColumn.getOptionValue(option) : option;
    const labelOf = (option: ValueOptions) =>
        listColumn.getOptionLabel
            ? listColumn.getOptionLabel(option)
            : String(option);
    const labels = new Map<unknown, string>(
        options.map((option) => [valueOf(option), labelOf(option)])
    );
    return (entry) => labels.get(entry) ?? String(entry ?? '');
}

function textOperator(
    value: string,
    label: string,
    matches: TextMatch
): GridFilterOperator {
    return {
        // The text columns' own values: switching the filter from one of them
        // to this column keeps the operator, so "enthält" stays typed text.
        value,
        label,
        getApplyFilterFn: (filterItem, column) => {
            const typed =
                typeof filterItem.value === 'string'
                    ? filterItem.value.trim()
                    : '';
            if (!typed) {
                return null;
            }
            const labelOf = entryLabeller(column);
            return (cellValue, row, rowColumn, apiRef) => {
                if (Array.isArray(cellValue)) {
                    return cellValue.some((entry) =>
                        matches(labelOf(entry), typed)
                    );
                }
                // A single choice: what the cell shows, e.g. a project's
                // number and title rather than its id.
                const shown = apiRef.current.getRowFormattedValue(
                    row,
                    rowColumn
                );
                return shown != null && matches(String(shown), typed);
            };
        },
        InputComponent: GridFilterInputValue
    };
}

function textOperators(labels: ListFilterLabels): GridFilterOperator[] {
    return [
        textOperator('contains', labels.contains, TEXT_MATCHES.contains),
        textOperator('equals', labels.equals, TEXT_MATCHES.equals),
        textOperator('startsWith', labels.startsWith, TEXT_MATCHES.startsWith),
        textOperator('endsWith', labels.endsWith, TEXT_MATCHES.endsWith)
    ];
}

/**
 * A list column's filter: typed text first — "enthält 7000" as in any text
 * column, matched entry by entry — then MUI's pick from the options and the
 * empty checks. Without the text operators, a Befundnummer could only be
 * picked from a list of every number in the family.
 */
export function listFilterOperators(
    labels: ListFilterLabels
): GridFilterOperator[] {
    // MUI names the pick `contains`, the value the text operator above
    // needs; renamed, so `?finding=` uses `containsAnyOf`.
    const picks: Record<string, { value: string; label: string }> = {
        contains: { value: 'containsAnyOf', label: labels.containsAnyOf },
        doesNotContain: {
            value: 'doesNotContainAnyOf',
            label: labels.doesNotContainAnyOf
        }
    };
    return [
        ...textOperators(labels),
        ...getGridMultiSelectOperators().map((operator) =>
            picks[operator.value]
                ? { ...operator, ...picks[operator.value] }
                : operator
        )
    ];
}

/** Gives a `multiSelect` column the operators above, unless it brings its own. */
export function withListFilterOperators<Row extends GridValidRowModel>(
    column: GridColDef<Row>,
    operators: GridFilterOperator[]
): GridColDef<Row> {
    if (column.type !== 'multiSelect' || column.filterOperators) {
        return column;
    }
    return { ...column, filterOperators: operators };
}

function useFilterLabels(): ListFilterLabels {
    const intl = useIntl();
    return useMemo(() => {
        const translate = (id: string) => intl.formatMessage({ id });
        return {
            contains: translate('grid.listFilter.contains'),
            equals: translate('grid.listFilter.equals'),
            startsWith: translate('grid.listFilter.startsWith'),
            endsWith: translate('grid.listFilter.endsWith'),
            containsAnyOf: translate('grid.listFilter.containsAnyOf'),
            doesNotContainAnyOf: translate(
                'grid.listFilter.doesNotContainAnyOf'
            )
        };
    }, [intl]);
}

/** {@link listFilterOperators} in the UI's language. */
export function useListFilterOperators(): GridFilterOperator[] {
    const labels = useFilterLabels();
    return useMemo(() => listFilterOperators(labels), [labels]);
}

/**
 * For a `singleSelect` whose options grow with the data — projects, catalog
 * positions — typed text first, then MUI's pick. A fixed list (Status,
 * Befundart) keeps the pick alone: there, choosing is quicker than typing.
 */
export function useGrowingSelectOperators(): GridFilterOperator[] {
    const labels = useFilterLabels();
    return useMemo(
        () => [...textOperators(labels), ...getGridSingleSelectOperators()],
        [labels]
    );
}
